import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import {
  FooterWrapper,
  InnerFooter,
  Grid,
  Label,
  GridColumn,
  HiddenCheckbox,
  CollapsibleSection,
  Item,
  FooterRow,
  CopyRight,
  TermsAndPrivacy,
  SocialRow,
  PaymentMethods
} from './styles';
import FacebookSVG from '@spa/shared/svg/facebook';
import InstagramSVG from '@spa/shared/svg//instagram';
import YoutubeSVG from '@spa/shared/svg/youtube';
import PinterestSVG from '@spa/shared/svg/pinterest';
import UniplacesSVG from '@spa/shared/svg/uniplaces';
import { footerData } from '@spa/shared/constants/footerData';
import { getHomepageByLocale } from '@spa/shared/utils/redirect';
import TermlyConsentLink from '@spa/shared/components/TermlyConsentLink';
import { PAYMENT_METHODS } from '@spa/shared/constants/payment-methods';

const Footer = ({ t, language, withPaymentMethods = false, ...props }) => {
  const [isMounted, setIsMounted] = useState(false);
  const { pathname, locale } = useRouter();
  const domainUrl = getHomepageByLocale(pathname, locale);

  const onClickableElementClick = url => window.open(decodeURIComponent(window.atob(url)));
  const getClickableElement = useCallback(
    (url, translation, hideAnchorFromCrawlers) => {
      if (!hideAnchorFromCrawlers || !url) {
        return null;
      }

      return !isMounted ? (
        <span>{t(translation)}</span>
      ) : (
        <span onClick={() => onClickableElementClick(url)}>{t(translation)}</span>
      );
    },
    [isMounted]
  );

  useEffect(() => setIsMounted(true), []);

  return (
    <FooterWrapper {...props}>
      {withPaymentMethods && (
        <PaymentMethods>
          {PAYMENT_METHODS.map(({ name, Icon }) => (
            <Icon key={`${name}-footer-icon`} />
          ))}
        </PaymentMethods>
      )}
      <InnerFooter>
        <SocialRow>
          <a rel="noopener noreferrer" target="_blank" href={process.env.FACEBOOK_URL} title="Facebook">
            <FacebookSVG />
          </a>
          <a rel="noopener noreferrer" target="_blank" href={process.env.INSTAGRAM_URL} title="Instagram">
            <InstagramSVG />
          </a>
          <a rel="noopener noreferrer" target="_blank" href={process.env.YOUTUBE_URL} title="Youtube">
            <YoutubeSVG />
          </a>
          <a rel="noopener noreferrer" target="_blank" href={process.env.PINTEREST_URL} title="Pinterest">
            <PinterestSVG />
          </a>
        </SocialRow>
        <Grid>
          {footerData.map(({ title, items }) => (
            <GridColumn key={title}>
              <HiddenCheckbox id={title} type="checkbox" />
              <Label htmlFor={title}>{t(title)}</Label>
              <CollapsibleSection>
                {items.map(({ id, href, translation, link, dataEvent, hideAnchorFromCrawlers }) => (
                  <Item key={id}>
                    {href(language) && !hideAnchorFromCrawlers ? (
                      <a href={href(language)} rel="noopener noreferrer" target="_blank">
                        {t(translation)}
                      </a>
                    ) : (
                      getClickableElement(href(language), translation, hideAnchorFromCrawlers)
                    )}
                    {link && !hideAnchorFromCrawlers ? (
                      <Link href={link} data-event={dataEvent}>
                        {t(translation)}
                      </Link>
                    ) : (
                      getClickableElement(link, translation, hideAnchorFromCrawlers)
                    )}
                  </Item>
                ))}
              </CollapsibleSection>
            </GridColumn>
          ))}
        </Grid>
        <FooterRow>
          <CopyRight>
            <span className="copy-right" onClick={() => window.open(domainUrl)}>
              <UniplacesSVG />
              {` © ${new Date().getFullYear()} Uniplaces, ${t('footer:All rights reserved')}`}
            </span>
          </CopyRight>
          <TermsAndPrivacy>
            <span
              className="terms-of-service"
              onClick={() => window.open(`${process.env.APP_URL}${language}/terms/terms-of-service`)}
            >
              {t('footer:Terms')}
            </span>
            <span
              className="privacy-policy"
              onClick={() => window.open(`${process.env.APP_URL}${language}/terms/privacy-policy`)}
            >
              {t('footer:Privacy')}
            </span>
            <TermlyConsentLink className="termly" title={t('footer:Consent Preferences')} />
          </TermsAndPrivacy>
        </FooterRow>
      </InnerFooter>
    </FooterWrapper>
  );
};

Footer.propTypes = {
  t: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  withPaymentMethods: PropTypes.bool
};

export default Footer;
